import { useQuery } from '@tanstack/react-query'
import { CheckCircleIcon, WarningIcon } from '@phosphor-icons/react'
import { contractApi } from '@/lib/api/contracts.api'
import type { EligibleRetainer } from '@scrambl/shared'

interface RetainerChipProps {
  moduleType: string
  moduleId: string
}

// The endpoint answers `null` when nothing matches, which NestJS sends as an
// empty 200 body — apiClient turns that into `{ success: true }`. Verify the
// payload really is a retainer before reading its numbers.
function isEligibleRetainer(value: unknown): value is EligibleRetainer {
  return (
    !!value &&
    typeof value === 'object' &&
    typeof (value as EligibleRetainer).usagePercent === 'number' &&
    typeof (value as EligibleRetainer).contractTitle === 'string'
  )
}

// Read-only indicator showing which retainer this time entry will count toward.
// Reuses the same scope-resolution logic the period aggregator runs at recalc
// time — does not change billing behavior, just makes it visible to the user.
export function RetainerChip({ moduleType, moduleId }: RetainerChipProps) {
  const { data, isLoading } = useQuery({
    queryKey: ['eligible-retainer', moduleType, moduleId],
    queryFn: () => contractApi.getEligibleRetainerForModule(moduleType, moduleId),
    enabled: Boolean(moduleType && moduleId),
    staleTime: 60_000,
  })

  if (isLoading || data === undefined) return null

  const retainer = isEligibleRetainer(data) ? data : null

  // Nothing to say when the work counts toward no retainer — stay quiet
  // rather than adding a row of noise under every linked module.
  if (!retainer) return null

  const Icon = retainer.overage ? WarningIcon : CheckCircleIcon
  const tone = retainer.overage
    ? 'border-destructive/40 bg-destructive/5 text-destructive'
    : 'border-emerald-500/30 bg-emerald-500/5 text-emerald-700'

  return (
    <div className={`inline-flex items-center gap-1 rounded-md border px-2 py-0.5 text-[11px] ${tone}`}>
      <Icon className="h-3 w-3" />
      <span>
        Counts toward: <strong>{retainer.contractTitle}</strong>
        {retainer.planName ? ` (${retainer.planName})` : ''}
        {' · '}
        {retainer.usagePercent.toFixed(0)}% used
      </span>
    </div>
  )
}
